import * as yup from 'yup';
import { UseFormProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICompanyDetails, ICompanyCreation, IPrimaryCompanyDetails } from '@entities/companies'; // Import ICompanyDetail if not already imported
import {
  REQUIRED_BANK_ACCOUNT_NUMBER,
  REQUIRED_BANK_ADDRESS,
  REQUIRED_BANK_CODE,
  REQUIRED_BANK_NAME,
  REQUIRED_BASED_ON_DOCUMENT,
  REQUIRED_COMPANY_ADDRESS,
  REQUIRED_COUNTRY_NAME,
  REQUIRED_COUNTRY_NAME_ID,
  REQUIRED_NAME,
  REQUIRED_REGISTRATION_CODE,
  REQUIRED_REPRESENTATIVES_NAME,
  REQUIRED_ROLE,
  REQUIRED_VAT_NUMBER,
  INVALID_VAT_NUMBER,
  REQUIRED_FIELDS_LANGUAGE,
} from '../constants/form-error.constants';
import { reVatNumber } from '@shared/lib/regex';

const companyDetailSchema = yup.object().shape({
  name: yup.string().required(REQUIRED_NAME),
  registrationCode: yup.string().required(REQUIRED_REGISTRATION_CODE),
  representativesName: yup.string().required(REQUIRED_REPRESENTATIVES_NAME),
  role: yup.string().required(REQUIRED_ROLE),
  basedOnTheDocument: yup.string().required(REQUIRED_BASED_ON_DOCUMENT),
  bankName: yup.string().required(REQUIRED_BANK_NAME),
  bankAddress: yup.string().required(REQUIRED_BANK_ADDRESS),
  countryId: yup.string().required(REQUIRED_FIELDS_LANGUAGE),
  bankCode: yup.string().required(REQUIRED_BANK_CODE),
  bankAccountNumber: yup.string().required(REQUIRED_BANK_ACCOUNT_NUMBER),
  companyAddress: yup.string().required(REQUIRED_COMPANY_ADDRESS),
  countryName: yup.string().required(REQUIRED_COUNTRY_NAME),
});

const primaryCompanyDetailSchema = yup.object().shape({
  name: yup.string().required(REQUIRED_NAME),
  registrationCode: yup.string().required(REQUIRED_REGISTRATION_CODE),
  representativesName: yup.string().required(REQUIRED_REPRESENTATIVES_NAME),
  role: yup.string().required(REQUIRED_ROLE),
  basedOnTheDocument: yup.string().required(REQUIRED_BASED_ON_DOCUMENT),
  bankName: yup.string().required(REQUIRED_BANK_NAME),
  bankAddress: yup.string().required(REQUIRED_BANK_ADDRESS),
  countryId: yup.string().required(REQUIRED_COUNTRY_NAME_ID),
  bankCode: yup.string().required(REQUIRED_BANK_CODE),
  bankAccountNumber: yup.string().required(REQUIRED_BANK_ACCOUNT_NUMBER),
  companyAddress: yup.string().required(REQUIRED_COMPANY_ADDRESS),
  vatNumber: yup
    .string()
    .nullable()
    .test('required', REQUIRED_VAT_NUMBER, value => {
      return value !== '';
    })
    .test('valid-vat-number', INVALID_VAT_NUMBER, value => {
      if (value) {
        return reVatNumber.test(value);
      }

      return true;
    }),
});

const CompanyCreationSchema = yup.object().shape({
  primaryCompanyDetails: primaryCompanyDetailSchema,
  companyDetailsList: yup.array().of(companyDetailSchema).required(),
});

export const primaryDetailsDefaultValues: IPrimaryCompanyDetails = {
  name: '',
  registrationCode: '',
  representativesName: '',
  role: '',
  basedOnTheDocument: '',
  companyAddress: '',
  bankAccountNumber: '',
  bankName: '',
  bankAddress: '',
  bankCode: '',
  countryId: '',
  vatNumber: null,
};

export const companyDefaultValues: ICompanyDetails = {
  name: '',
  registrationCode: '',
  representativesName: '',
  role: '',
  basedOnTheDocument: '',
  companyAddress: '',
  bankAccountNumber: '',
  bankName: '',
  bankAddress: '',
  bankCode: '',
  countryId: '',
  countryName: '',
};

const defaultValues: ICompanyCreation = {
  primaryCompanyDetails: primaryDetailsDefaultValues,
  companyDetailsList: [companyDefaultValues],
};

export const options: UseFormProps<ICompanyCreation> = {
  mode: 'onBlur',
  defaultValues,
  resolver: yupResolver<ICompanyCreation>(CompanyCreationSchema),
};
